import { ResgateManager } from '@/newApi/services/ResgateManager';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { storeToRefs } from 'pinia';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import type { ResModel } from '@sysarb/resclient';
import { isResError, type ResError } from '@sysarb/resclient';
import { useIntervalFn, useWindowFocus } from '@vueuse/core';
import type { UserData, InstancesEntityData } from '@/login/types/AccessTokenData';
import { useUser } from '@/login/composables/useUser';
import { useRouter } from 'vue-router';
import { useRouterHelper } from '@/composables/useRouterHelper';
import type { InstanceRole } from '@sysarb/sysarb-types/services/v1/authentication';
import { ResgateSubscriptionManager } from '@/newApi/services/ResgateSubscriptionManager';
import { useNotificationStore } from '@/store/notificationStore';
import { Guid } from 'guid-typescript';
import { NotificationStates } from '@/models/enums/NotificationStates';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { api } from '@/api';
import { useLoginManager } from './useLoginManager';
import { RouteNames } from '@/models/enums/RouteNames';
import { EventService } from '@/services/EventService';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import { usePersonalDataAccess } from '@/composables/usePersonalDataAccess';
import { clearCachedPersonalData, clearStoredPersonalData } from '@/common/PersonalDataResetHelper';

const MAX_SIMULTANEOUS_INSTANCES_LOADING = 500;

function isResgateError(e: unknown): e is ResError {
    if (e == null) return false;
    if (!isResError(e)) return false;

    return true;
}

function isUserDataModel(data: unknown): data is UserData & ResModel {
    if (data == null) return false;
    if (typeof data !== 'object') return false;
    if (!('instances' in data)) return false;
    if (isResError(data)) return false;

    return true;
}

function isInstancesEntityDataModel(data: unknown): data is InstancesEntityData & ResModel {
    if (data == null) return false;
    if (typeof data !== 'object') return false;
    if (!('SubDomain' in data)) return false;
    if (isResError(data)) return false;

    return true;
}

/**
 * Composable for handling login data. Should only be used in the main Vue file.
 */
export function useLogin(): void {
    const loginStore = useLoginStore();
    const { userData, resgateAuthenticated, instanceTopic, isSupport, selectedInstance, tokenUserInfo, instance, isDev } = storeToRefs(loginStore);
    const loginManager = useLoginManager();
    const { logout, loginResgate } = loginManager;
    const { role, roles } = useUser();
    const router = useRouter();
    const instancesIsLoading = ref(false);
    const notifications = useNotificationStore();
    const { t } = useI18n();
    const focused = useWindowFocus();
    const { userTypeStartPageRoute } = useRouterHelper();
    const verifyQuery = useQuery(api.login.queries.validate(true));
    const queryClient = useQueryClient();
    const { canViewPersonalData } = usePersonalDataAccess();

    // Backend validation of token.
    const { pause, resume } = useIntervalFn(() => {
        void verifyQuery.refetch();
    }, 60000, { immediate: false });

    // WebSocket keepalive to prevent proxy/VPN idle connection drops.
    const { pause: pauseKeepalive, resume: resumeKeepalive } = useIntervalFn(() => {
        try {
            void ResgateManager.client.call('v1.system.keepalive', 'keepalive', {});
        } catch {
            // Silently ignore keepalive failures — this is only to keep the WebSocket alive.
        }
    }, 60000, { immediate: false });

    function errorHandler(e: unknown): void {
        if (isResgateError(e) && typeof e.method === 'string') {
            if (e._code === 'system.accessDenied') {
                // Unsubscribe all subscriptions for the specific resource.
                ResgateSubscriptionManager.unsubscribeRid(e.rid);

                // Access denied when working with access token is not acceptable. Logout the user.
                if (e.method.includes('access.token') || e.method.includes('sysarb.login')) {
                    console.error('LOGIN', 'Access token error', e);
                    pause();
                    return;
                }

                let rid = '';

                if (isDev.value) {
                    rid = `${e.rid}`;
                }

                notifications.add({
                    id: Guid.create(),
                    state: NotificationStates.Warning,
                    message: t('system.notAllowed') + rid,
                    created: new Date(),
                    isPreviewHidden: false
                });
            }
        }

        console.warn(e);
    }

    function userDataEventHandler(): void {
        // Does nothing! Nothing I say!
    }

    async function getUserData(): Promise<void> {
        if (tokenUserInfo.value == null) return;
        if (resgateAuthenticated.value === false) return;

        try {
            const result = await ResgateManager.client.get(`v2.access.token.${tokenUserInfo.value.userId}`);

            if (isUserDataModel(result)) {
                result.on('change', userDataEventHandler);
                loginStore.setUserData(result);
            } else {
                console.error('LOGIN', 'Failed to load user data', result);
                await logout();
            }
        } catch (e: unknown) {
            console.error('LOGIN', 'doLogin', e);
            await logout();
        }
    }

    async function loadInstances(): Promise<void> {
        if (instancesIsLoading.value === true) return;
        instancesIsLoading.value = true;

        if (userData.value == null) return;
        if (tokenUserInfo.value?.userId == null) return;

        if (userData.value.instances == null) {
            if (userData.value.calculatedInstances == null) {
                userData.value.calculatedInstances = [];
            } else {
                userData.value.calculatedInstances.length = 0;
            }

            loginStore.setInstancesLoading(0);
            return;
        }

        const instancesToLoad = Array.from(userData.value.instances);

        if (isSupport.value === true) {
            console.info('LOGIN', 'Support requires instance to be set', instanceTopic.value);
            instancesToLoad.length = 0;

            // We only load already selected instance. This is to speed up the loading process for support.
            if (instanceTopic.value != null) {
                instancesToLoad.push({ _rid: `v2.access.token.${tokenUserInfo.value.userId}.instance.${instanceTopic.value}`, soft: true });
            }
        }

        const totalInstances = instancesToLoad.length;
        loginStore.setInstancesLoading(totalInstances);

        if (userData.value.calculatedInstances == null) {
            userData.value.calculatedInstances = [];
        } else {
            userData.value.calculatedInstances.length = 0;
        }

        let i = 0;

        const promises: Promise<unknown>[] = [];

        instancesToLoad.sort((a, b) => {
            // Sort instances so the if there are one where id contains instanseTopic, it will be loaded first. This is to speed up the loading process for support.
            if (instanceTopic.value != null) {
                if (a._rid.includes(instanceTopic.value)) return -1;
                if (b._rid.includes(instanceTopic.value)) return 1;
            }

            return 0;
        });

        for (const instanceRef of instancesToLoad) {
            promises.push(ResgateManager.client.get(instanceRef._rid).then(r => {
                if (userData.value == null) return;
                if (isInstancesEntityDataModel(r)) {
                    if (userData.value.calculatedInstances?.map(iv => iv.SubDomain).includes(r.SubDomain) === true) return;
                    userData.value.calculatedInstances?.push(r);
                    r.on('change', userDataEventHandler);
                }
            }).catch((e: unknown) => {
                console.error('LOGIN', 'loadInstances', e);
            })
                .finally(() => {
                    loginStore.setInstancesLoading(loginStore.instancesLoading - 1);
                }));

            i++;

            if (i % MAX_SIMULTANEOUS_INSTANCES_LOADING === 0 || i >= totalInstances) {
                await Promise.all(promises);
                promises.length = 0;
            }
        }

        try {
            await Promise.all(promises);
        } catch (e: unknown) {
            console.error('LOGIN', 'loadInstances', e);
        }

        // Only one instance is loaded so we select that one.
        if (userData.value.calculatedInstances.length === 1) {
            void queryClient.cancelQueries({ fetchStatus: 'fetching' });
            loginStore.setInstance(userData.value.calculatedInstances[0].SubDomain);
        }

        // Check that the selected instance is in the list of calculated instances. If not we unset the selected instance.
        if (instance.value != null && userData.value.calculatedInstances.some(iv => iv.SubDomain.toLowerCase() === instance.value?.toLowerCase()) === false) {
            loginStore.setInstance(null);
        }

        instancesIsLoading.value = false;
    }

    function redirectUser(): void {

        if (roles.value.length >= 1 && role.value == null) {
            console.info('LOGIN', 'Redirecting to role selection page');
            void router.push({ name: RouteNames.SelectRole });
            return;
        }

        if (role.value == null) {
            console.error('LOGIN', 'Role is null');
            return;
        }

        if (router.currentRoute.value.query.url != null) {
            console.info('LOGIN', 'Redirecting to target url');
            void router.push(router.currentRoute.value.query.url.toString());
            return;
        }

        console.info('LOGIN', 'Redirecting to user type start page');
        void router.push(userTypeStartPageRoute());
    }

    async function resetTanstack(): Promise<void> {
        // Clear Tanstack cache to prevent access issues with old data after login/logout
        // This is important when switching users or instances to ensure that the cache does not contain data that the new user should not have access to
        // or to prevent access issues due to stale data in the cache
        // Note: This will clear all cached queries, so use with caution if there are any long-lived queries that should not be cleared on login/logout
        await queryClient.cancelQueries({ fetchStatus: 'fetching' });
        queryClient.clear();
    }

    watch(() => focused.value, async (to) => {
        if (to === true) {
            void verifyQuery.refetch();
        }
    });

    watch((): InstancesEntityData | null => selectedInstance.value, async (to, from): Promise<void> => {
        await resetTanstack();
        ResgateSubscriptionManager.unsubscribeAll();
        await loginResgate();

        if (to == null && from != null) {
            await EventService.getInstance(router).unsubscribeAll(from.SubDomain);
        }

        redirectUser();
    });

    async function resetRoleSession(): Promise<void> {
        await resetTanstack();
        await clearCachedPersonalData(router);
        ResgateSubscriptionManager.unsubscribeAll();
        await loginResgate();
        redirectUser();
    }

    watch((): InstanceRole | null => role.value, async () => {
        if (role.value != null && selectedInstance.value != null) {
            await resetRoleSession();
        }
    }, { deep: true });

    // Only a change of an already loaded setting, and only for the role it applies to.
    watch(() => selectedInstance.value?.settings?.ShowPersonalDataForReadOnly, async (to, from) => {
        if (to == null || from == null || role.value?.role !== Role.ReadOnly) return;
        await resetRoleSession();
    });

    watch(canViewPersonalData, (canView) => {
        if (canView) return;
        clearStoredPersonalData();
    }, { immediate: true });

    async function logoutSystem(): Promise<void> {
        if (userData.value != null) {
            userData.value.off('change', userDataEventHandler);

            for (const i of userData.value.calculatedInstances ?? []) {
                i.off('change', userDataEventHandler);
            }
        }

        void queryClient.cancelQueries({ fetchStatus: 'fetching' });
        queryClient.clear();

        ResgateSubscriptionManager.unsubscribeAll();

        ResgateManager.disconnect();
        ResgateManager.destroy();

        pause();
        pauseKeepalive();
        loginStore.clear();

        console.info('LOGIN', 'User logged out');
        void router.push({ name: RouteNames.Login, query: { url: router.currentRoute.value.fullPath } });
    }

    async function loginSystem(): Promise<void> {
        resume();
        resumeKeepalive();
        await ResgateManager.connect(loginManager);
        ResgateManager.client.on('error', errorHandler);
        await getUserData();
        loginStore.setLoggingIn(false);
        void loadInstances();
    }

    watch(() => tokenUserInfo.value?.userId, async (to, from) => {
        if (to == null && from == null) return;

        await resetTanstack();

        if (tokenUserInfo.value?.userId != null) {
            await loginSystem();
        } else {
            await logoutSystem();
        }
    }, { immediate: true, deep: true });

    onMounted(async () => {
        await verifyQuery.refetch();

        if (userData.value != null) {
            resume();
            resumeKeepalive();
        }
    });

    onUnmounted(() => {
        pause();
        pauseKeepalive();
    });
}
